/**
 * La Tienda Del Padel — Quick View Drawer
 * Fetches product data via the JSON API and renders a slide-in drawer
 * with image gallery, variant selector, quantity picker, and Add to Cart.
 */

class QuickView {
  constructor() {
    this.drawer = document.getElementById('quick-view-drawer');
    if (!this.drawer) return;

    this.overlay = this.drawer.querySelector('.quick-view__overlay');
    this.closeBtn = this.drawer.querySelector('[data-quick-view-close]');
    this.contentEl = this.drawer.querySelector('.quick-view__content');
    this.previousActiveElement = null;
    this.currentProduct = null;
    this.selectedVariant = null;
    this.quantity = 1;
    this.currentImageIndex = 0;
    this.cache = {};

    this.init();
  }

  init() {
    // Bind quick view buttons (delegated)
    document.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-quick-view]');
      if (!btn) return;
      e.preventDefault();
      e.stopPropagation();
      const handle = btn.dataset.quickView;
      if (handle) this.open(handle);
    });

    // Close button
    if (this.closeBtn) {
      this.closeBtn.addEventListener('click', () => this.close());
    }

    // Overlay click
    if (this.overlay) {
      this.overlay.addEventListener('click', () => this.close());
    }

    // Escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.drawer.classList.contains('is-open')) {
        this.close();
      }
    });
  }

  /* =============================================
     OPEN / CLOSE
     ============================================= */

  async open(handle) {
    this.previousActiveElement = document.activeElement;
    this.quantity = 1;
    this.currentImageIndex = 0;

    // Show drawer with loading state
    this.contentEl.innerHTML = `
      <div class="quick-view__loading">
        <div class="quick-view__loading-spinner"></div>
        <span>Loading product...</span>
      </div>
    `;
    this.drawer.classList.add('is-open');
    document.body.classList.add('no-scroll');

    // Focus close button
    requestAnimationFrame(() => {
      if (this.closeBtn) this.closeBtn.focus();
    });

    // Fetch product data
    try {
      const product = await this.fetchProduct(handle);
      this.currentProduct = product;
      this.selectedVariant = product.variants.find(v => v.available) || product.variants[0];
      this.renderContent(product);
    } catch (err) {
      console.error('Quick view fetch error:', err);
      this.contentEl.innerHTML = `
        <div class="quick-view__loading">
          <span>Failed to load product. Please try again.</span>
        </div>
      `;
    }
  }

  close() {
    this.drawer.classList.remove('is-open');
    document.body.classList.remove('no-scroll');

    if (this.previousActiveElement && this.previousActiveElement.focus) {
      this.previousActiveElement.focus();
    }
  }

  /* =============================================
     DATA FETCHING
     ============================================= */

  async fetchProduct(handle) {
    if (this.cache[handle]) return this.cache[handle];

    // Must be the .js endpoint: .json has no variant `available` flag and
    // returns prices as decimal strings instead of cents.
    const response = await fetch(`/products/${handle}.js`);
    if (!response.ok) throw new Error(`HTTP ${response.status}`);

    const product = await response.json();
    this.cache[handle] = product;
    return product;
  }

  /* =============================================
     RENDERING
     ============================================= */

  renderContent(product) {
    const images = product.images || [];
    const variant = this.selectedVariant;
    const price = variant ? variant.price : product.variants[0].price;
    const comparePrice = variant ? variant.compare_at_price : product.variants[0].compare_at_price;

    // Strip HTML tags from description and truncate
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = product.description || '';
    const plainDesc = tempDiv.textContent || tempDiv.innerText || '';
    const truncDesc = plainDesc.length > 250 ? plainDesc.substring(0, 250) + '...' : plainDesc;

    let html = '';

    // Image gallery
    if (images.length > 0) {
      html += `<div class="quick-view__image-wrapper">`;
      html += `<img class="quick-view__image" src="${this.escapeAttr(images[this.currentImageIndex])}" alt="${this.escapeAttr(product.title)}" id="quick-view-main-image">`;

      if (images.length > 1) {
        html += `
          <button class="quick-view__image-arrow quick-view__image-arrow--prev" type="button" data-qv-image-prev aria-label="Previous image">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" aria-hidden="true"><polyline points="15 18 9 12 15 6"/></svg>
          </button>
          <button class="quick-view__image-arrow quick-view__image-arrow--next" type="button" data-qv-image-next aria-label="Next image">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" aria-hidden="true"><polyline points="9 18 15 12 9 6"/></svg>
          </button>
        `;
        html += '<div class="quick-view__image-nav">';
        images.forEach((img, i) => {
          html += `<button class="quick-view__image-dot${i === this.currentImageIndex ? ' is-active' : ''}" type="button" data-qv-image-index="${i}" aria-label="View image ${i + 1}"></button>`;
        });
        html += '</div>';
      }
      html += '</div>';
    }

    // Product info
    html += '<div class="quick-view__info">';

    // Vendor
    if (product.vendor) {
      html += `<div class="quick-view__vendor">${this.escapeHtml(product.vendor)}</div>`;
    }

    // Title
    html += `<h2 class="quick-view__title">${this.escapeHtml(product.title)}</h2>`;

    // Price
    html += '<div class="quick-view__price">';
    html += `<span class="quick-view__price-current">${this.formatMoney(price)}</span>`;
    if (comparePrice && comparePrice > price) {
      const discountPct = Math.round(((comparePrice - price) / comparePrice) * 100);
      html += `<span class="quick-view__price-compare">${this.formatMoney(comparePrice)}</span>`;
      if (discountPct > 5) {
        html += `<span class="quick-view__price-badge">-${discountPct}%</span>`;
      }
    }
    html += '</div>';

    // Description
    if (truncDesc) {
      html += `<p class="quick-view__description">${this.escapeHtml(truncDesc)}</p>`;
    }

    // Variant selector
    if (product.options && product.variants.length > 1) {
      html += '<div class="quick-view__variants">';
      product.options.forEach((option, optIdx) => {
        html += `<div class="quick-view__variant-group">`;
        html += `<div class="quick-view__variant-label">${this.escapeHtml(option.name)}</div>`;
        html += '<div class="quick-view__variant-options">';

        // Get unique values for this option
        const uniqueValues = [];
        product.variants.forEach(v => {
          const val = v.options ? v.options[optIdx] : (v[`option${optIdx + 1}`] || '');
          if (val && !uniqueValues.includes(val)) {
            uniqueValues.push(val);
          }
        });

        const selectedValue = this.selectedVariant
          ? (this.selectedVariant.options ? this.selectedVariant.options[optIdx] : (this.selectedVariant[`option${optIdx + 1}`] || ''))
          : '';

        uniqueValues.forEach(val => {
          const isSelected = val === selectedValue;
          html += `<button class="quick-view__variant-option${isSelected ? ' is-selected' : ''}" type="button" data-qv-option-index="${optIdx}" data-qv-option-value="${this.escapeAttr(val)}">${this.escapeHtml(val)}</button>`;
        });

        html += '</div></div>';
      });
      html += '</div>';
    }

    // Quantity selector
    html += `
      <div class="quick-view__quantity">
        <span class="quick-view__quantity-label">Qty</span>
        <div class="quick-view__quantity-control">
          <button class="quick-view__quantity-btn" type="button" data-qv-qty-minus aria-label="Decrease quantity">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" aria-hidden="true"><line x1="5" y1="12" x2="19" y2="12"/></svg>
          </button>
          <span class="quick-view__quantity-value" data-qv-qty-display>${this.quantity}</span>
          <button class="quick-view__quantity-btn" type="button" data-qv-qty-plus aria-label="Increase quantity">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" aria-hidden="true"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
          </button>
        </div>
      </div>
    `;

    // Add to Cart button
    const available = this.selectedVariant && this.selectedVariant.available;
    html += `<button class="quick-view__add-btn" type="button" data-qv-add-to-cart ${!available ? 'disabled' : ''}>${available ? 'Add to Cart' : 'Sold Out'}</button>`;

    // View Full Details link
    html += `<a href="/products/${this.escapeAttr(product.handle)}" class="quick-view__details-link">View Full Details &rarr;</a>`;

    html += '</div>'; // .quick-view__info

    this.contentEl.innerHTML = html;
    this.bindContentEvents();
  }

  /* =============================================
     CONTENT EVENT BINDING
     ============================================= */

  bindContentEvents() {
    const product = this.currentProduct;
    if (!product) return;

    // Image navigation
    const images = product.images || [];
    if (images.length > 1) {
      const prevBtn = this.contentEl.querySelector('[data-qv-image-prev]');
      const nextBtn = this.contentEl.querySelector('[data-qv-image-next]');

      if (prevBtn) {
        prevBtn.addEventListener('click', () => {
          this.currentImageIndex = (this.currentImageIndex - 1 + images.length) % images.length;
          this.updateImage(images);
        });
      }

      if (nextBtn) {
        nextBtn.addEventListener('click', () => {
          this.currentImageIndex = (this.currentImageIndex + 1) % images.length;
          this.updateImage(images);
        });
      }

      this.contentEl.querySelectorAll('[data-qv-image-index]').forEach(dot => {
        dot.addEventListener('click', () => {
          this.currentImageIndex = parseInt(dot.dataset.qvImageIndex, 10);
          this.updateImage(images);
        });
      });
    }

    // Variant selection
    this.contentEl.querySelectorAll('[data-qv-option-value]').forEach(btn => {
      btn.addEventListener('click', () => {
        const optIdx = parseInt(btn.dataset.qvOptionIndex, 10);
        const optVal = btn.dataset.qvOptionValue;

        // Build selected options array
        const selectedOptions = [];
        product.options.forEach((opt, i) => {
          if (i === optIdx) {
            selectedOptions.push(optVal);
          } else {
            // Get currently selected value for this option
            const currentSelected = this.contentEl.querySelector(`[data-qv-option-index="${i}"].is-selected`);
            selectedOptions.push(currentSelected ? currentSelected.dataset.qvOptionValue : '');
          }
        });

        // Find matching variant
        const matchedVariant = product.variants.find(v => {
          const vOpts = v.options || [v.option1, v.option2, v.option3].filter(Boolean);
          return selectedOptions.every((opt, i) => vOpts[i] === opt);
        });

        if (matchedVariant) {
          this.selectedVariant = matchedVariant;

          // Update variant image if exists (.js images are plain URLs, so
          // locate the variant image by its 1-based position in the gallery)
          if (matchedVariant.featured_image && matchedVariant.featured_image.position) {
            const imgIdx = matchedVariant.featured_image.position - 1;
            if (imgIdx >= 0 && imgIdx < images.length) {
              this.currentImageIndex = imgIdx;
              this.updateImage(images);
            }
          }
        }

        // Re-render content to reflect changes
        this.renderContent(product);
      });
    });

    // Quantity
    const qtyMinus = this.contentEl.querySelector('[data-qv-qty-minus]');
    const qtyPlus = this.contentEl.querySelector('[data-qv-qty-plus]');
    const qtyDisplay = this.contentEl.querySelector('[data-qv-qty-display]');

    if (qtyMinus) {
      qtyMinus.addEventListener('click', () => {
        if (this.quantity > 1) {
          this.quantity--;
          if (qtyDisplay) qtyDisplay.textContent = this.quantity;
        }
      });
    }

    if (qtyPlus) {
      qtyPlus.addEventListener('click', () => {
        if (this.quantity < 10) {
          this.quantity++;
          if (qtyDisplay) qtyDisplay.textContent = this.quantity;
        }
      });
    }

    // Add to Cart
    const addBtn = this.contentEl.querySelector('[data-qv-add-to-cart]');
    if (addBtn) {
      addBtn.addEventListener('click', () => this.addToCart(addBtn));
    }
  }

  updateImage(images) {
    const mainImg = this.contentEl.querySelector('#quick-view-main-image');
    if (mainImg && images[this.currentImageIndex]) {
      mainImg.src = images[this.currentImageIndex];
    }

    // Update dots
    this.contentEl.querySelectorAll('[data-qv-image-index]').forEach(dot => {
      dot.classList.toggle('is-active', parseInt(dot.dataset.qvImageIndex, 10) === this.currentImageIndex);
    });
  }

  /* =============================================
     ADD TO CART
     ============================================= */

  async addToCart(btn) {
    if (!this.selectedVariant || !this.selectedVariant.available) return;

    btn.classList.add('is-loading');
    btn.disabled = true;

    try {
      const formData = new FormData();
      formData.append('id', this.selectedVariant.id);
      formData.append('quantity', this.quantity.toString());

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        body: formData
      });

      if (response.ok) {
        const addData = await response.json();

        // GA4: add_to_cart event + Meta Pixel: AddToCart
        // Shopify returns the line's cumulative quantity; analytics needs what
        // was actually added on this click, which is this.quantity.
        {
          const addedQty = this.quantity || 1;
          const lines = Array.isArray(addData.items) ? addData.items : [addData];
          const items = lines.map(l => ({
            item_id: String(l.product_id || l.id),
            item_name: l.product_title || l.title,
            item_brand: l.vendor || undefined,
            item_category: l.product_type || undefined,
            item_variant: l.variant_title || undefined,
            price: (l.price || 0) / 100,
            quantity: addedQty
          }));
          const totalValue = items.reduce((s, i) => s + i.price * i.quantity, 0);
          if (typeof window.gtag === 'function') {
            window.gtag('event', 'add_to_cart', {
              currency: 'EGP',
              value: totalValue,
              items
            });
          }
          if (typeof window.fbq === 'function') {
            window.fbq('track', 'AddToCart', {
              content_ids: items.map(i => i.item_id),
              content_type: 'product',
              content_name: items[0] && items[0].item_name,
              currency: 'EGP',
              value: totalValue
            });
          }
        }

        btn.classList.remove('is-loading');
        btn.textContent = 'Added!';
        btn.style.background = '#0cc45f';

        // Bounce cart icon in header
        const cartBtn = document.querySelector('.site-header__action-btn--cart');
        if (cartBtn) {
          cartBtn.classList.add('cart-icon--bounce');
          setTimeout(() => cartBtn.classList.remove('cart-icon--bounce'), 600);
        }

        // Show "Added!" for 1.2s as user feedback, then close the quick-view
        // modal and trigger the drawer refresh+open. refreshCartDrawer awaits
        // the refresh before opening so the drawer never appears with stale
        // contents (the previous code fired refresh + 1.2s timer in parallel,
        // which could race on slow connections).
        setTimeout(() => {
          btn.textContent = 'Add to Cart';
          btn.style.background = '';
          btn.disabled = false;
          this.close();
          window.refreshCartDrawer();
        }, 1200);
      } else {
        const error = await response.json();
        btn.classList.remove('is-loading');
        btn.textContent = error.description || 'Error';
        setTimeout(() => {
          btn.textContent = 'Add to Cart';
          btn.disabled = false;
        }, 1500);
      }
    } catch (err) {
      console.error('Quick view add to cart error:', err);
      btn.classList.remove('is-loading');
      btn.textContent = 'Add to Cart';
      btn.disabled = false;
    }
  }

  /* =============================================
     UTILITY FUNCTIONS
     ============================================= */

  formatMoney(cents) {
    if (!cents || cents === '' || cents === null) return '';
    if (typeof cents === 'string') cents = parseInt(cents, 10);
    if (isNaN(cents)) return '';
    const amount = (cents / 100).toFixed(2);
    const formatted = parseFloat(amount).toLocaleString();
    return `${this.getCurrencySymbol()}${formatted}`;
  }

  getCurrencySymbol() {
    const existing = document.querySelector('.price__regular, .price__sale');
    if (existing) {
      const text = existing.textContent.trim();
      const match = text.match(/^([^\d]+)/);
      if (match) return match[1];
    }
    return 'LE ';
  }

  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text || '';
    return div.innerHTML;
  }

  escapeAttr(text) {
    return (text || '').replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/'/g, '&#39;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  window.quickView = new QuickView();
});
